import {useMutation} from "@apollo/client";
import {useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {toast} from "react-hot-toast";

import Button from "@/components/button/Button.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {GQL_SEND_MAKE_WEBHOOK_TEST} from "@/lib/queries/integrations.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

const MAKE_URL_REGEX = /^https:\/\/[^/]*\.make\.com\//i;

interface MakeFormProps {
  serverErrors?: ServerErrors;
}

export const MakeForm = ({serverErrors}: MakeFormProps) => {
  const {setError, watch} = useFormContext();
  const [isSending, setIsSending] = useState(false);
  const [sendMakeWebhookTest] = useMutation(GQL_SEND_MAKE_WEBHOOK_TEST);

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  const webhookUrl = ((watch("settings.webhook_url") ?? "") as string).trim();
  const canTest = MAKE_URL_REGEX.test(webhookUrl);

  const sendTest = async () => {
    if (!canTest || isSending) {
      return;
    }
    setIsSending(true);
    try {
      const {data} = await sendMakeWebhookTest({variables: {webhookUrl}});
      if (data?.response?.status === "SUCCESS") {
        toast.success("Test payload sent. Make.com should now have the data structure.");
      } else {
        toast.error(data?.response?.message ?? "Failed to send test payload.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send test payload.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <div className="pb-2">
        <p className="mb-2">
          Paste the Custom Webhook URL from your Make.com scenario. The same URL is used for both alert raised and alert
          cleared events &mdash; every payload includes an <code>data.alert.is_up</code> boolean (and a matching{" "}
          <code>event</code> field) so you can add a router or filter in your scenario to branch on raise vs. recovery
          and trigger different downstream actions.
        </p>
      </div>
      <div className="mb-3">
        <InputField
          name="settings.webhook_url"
          label="Webhook URL"
          placeholder="https://hook.eu1.make.com/…"
          tooltip="Custom webhook URL from the Make.com scenario that should run when an alert is raised or cleared."
          autoFocus={true}
        />
        {canTest ? (
          <div className="mt-1">
            <Button variant="linkBlue" size="text" type="button" disabled={isSending} onClick={sendTest}>
              {isSending ? "Sending test payload…" : "Send test payload to this URL"}
            </Button>
          </div>
        ) : (
          <div className="pulseTextBodyTiny pulse-color-gray-600 mt-1">
            Enter a valid <code>https://*.make.com/</code> URL to enable the test payload link.
          </div>
        )}
      </div>

      <SetupInfo>
        <div className="p-2">
          In Make.com, create a scenario whose first module is{" "}
          <span className="pulseTextBodyBold">Webhooks &rarr; Custom webhook</span>. Copy the generated URL (it looks
          like <code>https://hook.eu1.make.com/&lt;hash&gt;</code>) and paste it above.
        </div>
        <div className="p-2">
          <span className="pulseTextBodyBold">Sampling the payload.</span> Make.com only exposes the alert fields in its
          variable picker after it has seen a real sample. Click{" "}
          <span className="pulseTextBodyBold">Re-determine data structure</span> on the Custom webhook module in
          Make.com, then click the <span className="pulseTextBodyBold">Send test payload</span> link beneath the field
          above. Make.com will capture the sample and the alert fields become draggable into downstream modules (Slack,
          Teams, email, etc.).
        </div>
        <div className="p-2">
          <span className="pulseTextBodyBold">Branching on raise vs. recovery.</span> Because a single webhook URL
          receives both events, use a Make.com <span className="pulseTextBodyBold">Router</span> (or a{" "}
          <span className="pulseTextBodyBold">Filter</span> on each downstream route) keyed on{" "}
          <code>data.alert.is_up</code> &mdash; <code>false</code> for a raised alert, <code>true</code> for a recovery.
          The <code>event</code> field (<code>alert_raised</code> / <code>alert_cleared</code>) carries the same
          information if you prefer matching on a string.
        </div>
        <div className="p-2">
          The most useful payload fields are:
          <ul className="mb-0 mt-2">
            <li>
              <code>data.alert.is_up</code> &mdash; boolean, <code>true</code> on recovery, <code>false</code> when an
              alert is raised (use this to branch)
            </li>
            <li>
              <code>event</code> &mdash; <code>alert_raised</code> or <code>alert_cleared</code> (string equivalent of{" "}
              <code>is_up</code>)
            </li>
            <li>
              <code>data.alert.short_output</code> &mdash; one-line state message (best for chat messages)
            </li>
            <li>
              <code>data.alert.output</code> &mdash; full state message with diagnostic detail
            </li>
            <li>
              <code>data.service.display_name</code>, <code>data.service.msp_address</code> &mdash; check name and
              monitored address
            </li>
            <li>
              <code>data.links</code> &mdash; deep links back to the check and alert in Uptime.com
            </li>
          </ul>
        </div>
        <div className="p-2">
          <a target="_blank" href="https://support.uptime.com/hc/en-us/articles/make-integration" rel="noreferrer">
            View {IntegrationModuleInfo[IntegrationModules.MAKE].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
